<!-- src/components/forms/completed/AccidentCompleted.vue -->
<template>
  <div class="bg-white p-2 sm:p-4 md:p-8 space-y-12 animate-fadeIn text-left min-h-screen">
    
    <!-- 1. Statutory Status Header (PDF Ready) -->
    <div class="flex flex-wrap items-center justify-between gap-4 p-8 bg-slate-50 rounded-[32px] border border-slate-200 shadow-inner">
      <div class="flex items-center gap-3">
        <div class="bg-red-500 w-3 h-3 rounded-full animate-pulse"></div>
        <span class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">
          Record Type: Statutory Accident Report
        </span>
      </div>
      <div class="flex items-center gap-2 bg-[#0A1630] px-6 py-3 rounded-2xl shadow-lg">
        <span class="text-[10px] font-black uppercase tracking-[0.2em] text-[#20FCF1]">Incident Ref:</span>
        <span class="text-sm font-black text-white tracking-widest">{{ submissionData.humanReadableId || submissionData.referenceNumber }}</span>
      </div>
    </div>

    <!-- 2. Reporter Identity -->
    <section>
      <div class="flex items-center gap-3 mb-8">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <IdentificationIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">1. Reporter Identity</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="StatCard">
          <span class="label">Full Name</span>
          <span class="value">{{ submissionData.reporterName || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Email Address</span>
          <span class="value lowercase text-xs">{{ submissionData.reporterEmail || 'N/A' }}</span>
        </div>
        <div class="StatCard">
          <span class="label">Contact Phone</span>
          <span class="value">{{ submissionData.reporterPhone || 'N/A' }}</span>
        </div>
      </div>
    </section>

    <!-- 3. Incident Narrative (Sequential Analysis Registry) -->
    <section>
      <div class="flex items-center gap-3 mb-8">
        <div class="bg-[#0A1630] p-2.5 rounded-xl shadow-sm">
          <ChatBubbleLeftRightIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">2. Incident Narrative</h3>
      </div>

      <div class="space-y-8">
        <!-- Task Context -->
        <div class="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm">
          <div class="flex items-center gap-2 mb-4">
            <ClipboardDocumentCheckIcon class="h-5 w-5 text-[#20FCF1]" />
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Task Context</h4>
          </div>
          <p class="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{{ submissionData.taskContext || 'No context recorded.' }}</p>
        </div>

        <!-- Sequence of Events -->
        <div class="bg-white border border-slate-200 rounded-[32px] p-8 shadow-sm">
          <div class="flex items-center gap-2 mb-4">
            <QueueListIcon class="h-5 w-5 text-[#20FCF1]" />
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Sequence of Events</h4>
          </div>
          <p class="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{{ submissionData.sequenceOfEvents || 'No sequence recorded.' }}</p>
        </div>

        <!-- Immediate Response & Outcome -->
        <div class="bg-[#F8FAFC] border border-slate-200 rounded-[32px] p-8 shadow-inner">
          <div class="flex items-center gap-2 mb-4">
            <ExclamationCircleIcon class="h-5 w-5 text-rose-500" />
            <h4 class="text-[10px] font-black uppercase tracking-widest text-[#0A1630]">Immediate Response & Injury Outcome</h4>
          </div>
          <p class="text-sm text-slate-700 font-medium leading-relaxed whitespace-pre-wrap">{{ submissionData.immediateResponseOutcome || 'No outcome recorded.' }}</p>
        </div>
      </div>
    </section>

    <!-- 4. Incident Parameters -->
    <section>
      <div class="flex items-center gap-3 mb-8">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <CalendarDaysIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">3. Incident Parameters</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100 shadow-inner">
        <div class="space-y-4">
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Date & Time</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ formatDate(submissionData.accidentDateTime) }}</span>
          </div>
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Location</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ submissionData.location }}</span>
          </div>
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Body Part Affected</span>
            <span class="text-sm font-bold text-[#0A1630] uppercase">{{ formatText(submissionData.bodyPart) }}</span>
          </div>
        </div>
        <div class="space-y-4">
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Accident Type</span>
            <span class="text-sm font-bold text-[#0A1630]">{{ formatText(submissionData.accidentType) }}</span>
          </div>
          <div class="flex justify-between border-b border-slate-200 pb-2">
            <span class="text-[10px] font-black uppercase text-slate-400">Injury Type</span>
            <span class="text-sm font-bold text-red-600">{{ formatText(submissionData.injuryType) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- 5. Treatment & Medical Summary -->
    <section>
      <div class="flex items-center gap-3 mb-8">
        <div class="bg-[#0A1630] p-2.5 rounded-xl shadow-sm">
          <LifebuoyIcon class="h-6 w-6 text-[#20FCF1]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">4. Medical Intelligence</h3>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="StatCard" :class="submissionData.firstAidAdministered ? 'border-l-8 border-l-[#20FCF1]' : ''">
          <span class="label">First Aid Administered</span>
          <span class="value">{{ formatBoolean(submissionData.firstAidAdministered) }}</span>
        </div>
        <div class="StatCard" :class="submissionData.hospital ? 'border-l-8 border-l-red-500' : ''">
          <span class="label">Taken to Hospital</span>
          <span class="value">{{ formatBoolean(submissionData.hospital) }}</span>
        </div>
      </div>

      <div v-if="submissionData.firstAidAdministered" class="mt-6 p-8 bg-[#EBFDF8] border border-[#20FCF1]/20 rounded-[32px]">
        <p class="text-[10px] font-black uppercase tracking-widest text-[#0A1630] mb-3">First Aid Treatment Details</p>
        <p class="text-sm text-[#0A1630] leading-relaxed italic">{{ submissionData.firstAidDetails || 'No specific details recorded.' }}</p>
      </div>
    </section>

    <!-- 6. Witness Intelligence -->
    <section v-if="submissionData.witnessDetails">
      <div class="flex items-center gap-3 mb-8">
        <div class="bg-slate-200 p-2.5 rounded-xl shadow-sm">
          <UserGroupIcon class="h-6 w-6 text-slate-600" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">5. Witness Identification</h3>
      </div>
      <div class="bg-slate-50 border border-slate-200 rounded-[32px] p-8 shadow-inner">
        <p class="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{{ submissionData.witnessDetails }}</p>
      </div>
    </section>

    <!-- 7. Attachments & Evidence (PDF Ready) -->
    <section v-if="(submissionData.imageUrls && submissionData.imageUrls.length > 0) || (submissionData.documentUrls && submissionData.documentUrls.length > 0)">
      <div class="flex items-center gap-3 mb-8">
        <div class="bg-[#20FCF1] p-2.5 rounded-xl shadow-sm">
          <PaperClipIcon class="h-6 w-6 text-[#0A1630]" />
        </div>
        <h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630]">6. Evidence Attachments</h3>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Photo Grid -->
        <div v-if="submissionData.imageUrls?.length" class="p-8 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-6">Photographic Evidence</p>
          <div class="grid grid-cols-2 gap-4">
            <div v-for="(url, idx) in submissionData.imageUrls" :key="idx" class="group relative aspect-video overflow-hidden rounded-2xl border-4 border-white shadow-md">
              <img :src="url" class="h-full w-full object-cover" />
            </div>
          </div>
        </div>

        <!-- Document List -->
        <div v-if="submissionData.documentUrls?.length" class="p-8 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner">
          <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-6">Supporting Documentation</p>
          <div class="space-y-3">
            <div v-for="(url, idx) in submissionData.documentUrls" :key="idx" 
               class="flex items-center p-4 bg-white border border-slate-200 rounded-2xl shadow-sm">
               <component :is="getFileIcon(url)" class="h-5 w-5 text-slate-400 mr-4" />
               <span class="text-[10px] font-black text-slate-600 truncate flex-1 uppercase tracking-tighter">{{ getFileNameFromUrl(url) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 8. Attestation Sign-off -->
    <section class="border-t border-slate-100 pt-16">
      <div class="flex flex-col md:flex-row justify-between items-center gap-12">
        <div class="space-y-6 max-w-lg">
          <div class="flex items-center gap-3">
            <CheckCircleIcon class="h-6 w-6 text-[#20FCF1]" />
            <p class="text-[11px] font-black uppercase tracking-widest text-[#0A1630]">Statutory Attestation Complete</p>
          </div>
          <div class="p-8 bg-slate-900 rounded-[40px] border border-slate-800 shadow-2xl relative overflow-hidden">
            <div class="absolute top-0 right-0 p-6 opacity-5">
               <IdentificationIcon class="h-20 w-20 text-white" />
            </div>
            <p class="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Reporter Declaration</p>
            <p class="text-xs text-slate-300 font-medium italic leading-relaxed">"I formally confirm that the details provided in this accident report are a true and accurate reflection of the event."</p>
          </div>
        </div>

        <div v-if="submissionData.signatureDataUrl" class="bg-white p-8 rounded-[40px] border border-slate-200 shadow-2xl flex flex-col items-center min-w-[300px]">
          <p class="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-6">Digital Reporter Seal</p>
          <img :src="submissionData.signatureDataUrl" alt="Assessor Seal" class="max-h-24 object-contain mb-6 grayscale" />
          <div class="w-full h-px bg-slate-100 mb-6"></div>
          <div class="flex items-center gap-2 bg-[#EBFDF8] px-6 py-2 rounded-full border border-[#20FCF1]/20">
            <CheckBadgeIcon class="h-4 w-4 text-[#20FCF1]" />
            <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-widest">Identity Verified</span>
          </div>
          <p class="text-[9px] text-slate-400 mt-4 font-bold tracking-widest">{{ formatDate(submissionData.submittedAt) }}</p>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { 
  IdentificationIcon, CalendarDaysIcon, LifebuoyIcon, UserGroupIcon, 
  PaperClipIcon, CheckBadgeIcon, CheckCircleIcon,
  ChatBubbleLeftRightIcon, ClipboardDocumentCheckIcon,
  QueueListIcon, ExclamationCircleIcon
} from '@heroicons/vue/24/outline';

defineProps({
  submissionData: { type: Object, required: true },
  formatDate: { type: Function, required: true },
  formatText: { type: Function, required: true },
  formatBoolean: { type: Function, required: true },
  getFileIcon: { type: Function, required: true },
  getFileNameFromUrl: { type: Function, required: true }
});
</script>

<style scoped>
.StatCard { @apply flex flex-col p-8 bg-white border border-slate-200 rounded-[32px] shadow-sm; }
.StatCard .label { @apply text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2; }
.StatCard .value { @apply text-sm font-black text-[#0A1630] truncate tracking-tight; }
.animate-fadeIn { animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

@media print {
  .animate-fadeIn { animation: none !important; opacity: 1 !important; transform: none !important; }
}
</style>